import React from "react";
import { ReactNode } from "react";
import ObservableState from "../utils/ObservableState";
import * as ui from "../native";
import ObservableComponent from "./ObservableComponent";
import BaseUIProps, { copyBaseUIProps } from "../native/ui/BaseUIProps";
import ObjectObservable from "../utils/ObjectObservable";
import PopupTargetController from "./PopupTargetController";
import D3EAppTemplate from "../models/D3EAppTemplate";
import Popup from "./Popup";
import MaterialIcons from "../icons/MaterialIcons";
import PageNavigator from "../classes/PageNavigator";
import IconButton from "./IconButton";
import TemplateFeatureView from "./TemplateFeatureView";
import Button from "./Button";
import D3EFeature from "../models/D3EFeature";
import ListWrapper from "../utils/ListWrapper";
import FeatureDetailsView from "./FeatureDetailsView";
import OutlineFloatingActionButton from "./OutlineFloatingActionButton";
import TextView from "./TextView";
import CollectionUtils from "../utils/CollectionUtils";
import IconView from "./IconView";
import Divider from "./Divider";
import { BuildContext } from "../classes/BuildContext";

type _TemplateOverViewOnAddTemplate = (obj: D3EAppTemplate) => void;

type _MobileViewOnPressed = (d3eState: TemplateOverViewRefs) => void;

type _TabletViewOnPressed = (d3eState: TemplateOverViewRefs) => void;

type _AddTemplateButtonOnPressed = (d3eState: TemplateOverViewRefs) => void;

export interface TemplateOverViewProps extends BaseUIProps {
  key?: string;
  obj: D3EAppTemplate;
  onAddTemplate: _TemplateOverViewOnAddTemplate;
}
/// To store state data for TemplateOverView
class TemplateOverViewRefs {
  public addTemplateButton: AddTemplateButtonState =
    new AddTemplateButtonState();
  public mobileView: MobileViewState = new MobileViewState();
  public tabletView: TabletViewState = new TabletViewState();
}

interface AddTemplateButtonWithStateProps extends BaseUIProps {
  key?: string;
  d3eState: TemplateOverViewRefs;
  _addTemplateButtonHandler?: _AddTemplateButtonOnPressed;
}

class AddTemplateButtonState extends ObjectObservable {
  private _disable: boolean = false;
  public _hover: boolean = false;
  public _focus: boolean = false;
  public get disable(): boolean {
    return this._disable;
  }
  public setDisable(val: boolean) {
    let isValChanged: boolean = this._disable !== val;

    if (!isValChanged) {
      return;
    }

    this._disable = val;

    this.fire("disable", this);
  }
  public get hover(): boolean {
    return this._hover;
  }
  public setHover(val: boolean) {
    let isValChanged: boolean = this._hover !== val;

    if (!isValChanged) {
      return;
    }

    this._hover = val;

    this.fire("hover", this);
  }
  public get focus(): boolean {
    return this._focus;
  }
  public setFocus(val: boolean) {
    let isValChanged: boolean = this._focus !== val;

    if (!isValChanged) {
      return;
    }

    this._focus = val;

    this.fire("focus", this);
  }
}

class _AddTemplateButtonWithState extends ObservableComponent<AddTemplateButtonWithStateProps> {
  addTemplateButtonFocusNode: ui.FocusNode = new ui.FocusNode();
  static contextType = BuildContext;
  context: React.ContextType<typeof BuildContext>;
  public constructor(props: AddTemplateButtonWithStateProps) {
    super(props);

    this.initState();
  }
  public get addTemplateButton(): AddTemplateButtonState {
    return this.props.d3eState.addTemplateButton;
  }
  public get d3eState(): TemplateOverViewRefs {
    return this.props.d3eState;
  }
  public get _addTemplateButtonHandler(): _AddTemplateButtonOnPressed {
    return this.props._addTemplateButtonHandler;
  }
  public initState() {
    super.initState();

    this.updateObservable("addTemplateButton", null, this.addTemplateButton);

    this.initListeners();

    this.enableBuild = true;
  }
  public initListeners(): void {
    this.on(
      [
        "addTemplateButton",
        "addTemplateButton.",
        "addTemplateButton.focus",
        "addTemplateButton.hover",
      ],
      this.rebuild
    );
  }
  public addTemplateButtonOnEnter(event): void {
    return this.addTemplateButton.setHover(true);
  }
  public addTemplateButtonOnExit(event): void {
    return this.addTemplateButton.setHover(false);
  }
  public addTemplateButtonOnFocusChange(val): void {
    return this.addTemplateButton.setFocus(val);
  }
  public dispose(): void {
    this.addTemplateButton.setHover(false);

    this.addTemplateButton.setFocus(false);

    super.dispose();
  }
  public render(): ReactNode {
    let cStyle = this.context.theme;

    return ui.Container({
      margin: ui.EdgeInsets.all(10.0, new Map()),
      width: 90,
      child: Button({
        padding: cStyle.tButtonPrimaryButtonPaddingOn,
        decoration: this.addTemplateButton.hover
          ? cStyle.tButtonPrimaryButtonDecorationOnHover
          : this.addTemplateButton.focus
          ? cStyle.tButtonPrimaryButtonDecorationOnFocus
          : cStyle.tButtonPrimaryButtonDecorationOn,
        disable: this.addTemplateButton.disable,
        onPressed: () => {
          this._addTemplateButtonHandler(this.d3eState);
        },
        onFocusChange: (val) => {
          this.addTemplateButtonOnFocusChange(val);
        },
        child: TextView({
          data: "Add Template",
          style: new ui.TextStyle({ fontSize: 13 }),
          className: "x543 vc",
        }),
        onEnter: (event) => {
          this.addTemplateButtonOnEnter(event);
        },
        onExit: (event) => {
          this.addTemplateButtonOnExit(event);
        },
      }),
      className: "x17 hc vc",
    });
  }
}
function AddTemplateButtonWithState(props: AddTemplateButtonWithStateProps) {
  return React.createElement(_AddTemplateButtonWithState, props);
}

interface TabletViewWithStateProps extends BaseUIProps {
  key?: string;
  d3eState: TemplateOverViewRefs;
  _isWebViewHandler?: _TabletViewOnPressed;
  isWebView: boolean;
}

class TabletViewState extends ObjectObservable {
  private _disable: boolean = false;
  public get disable(): boolean {
    return this._disable;
  }
  public setDisable(val: boolean) {
    let isValChanged: boolean = this._disable !== val;

    if (!isValChanged) {
      return;
    }

    this._disable = val;

    this.fire("disable", this);
  }
}

class _TabletViewWithState extends ObservableComponent<TabletViewWithStateProps> {
  tabletViewFocusNode: ui.FocusNode = new ui.FocusNode();
  public constructor(props: TabletViewWithStateProps) {
    super(props);

    this.initState();
  }
  public get isWebView(): boolean {
    return this.props.isWebView;
  }
  public get tabletView(): TabletViewState {
    return this.props.d3eState.tabletView;
  }
  public get d3eState(): TemplateOverViewRefs {
    return this.props.d3eState;
  }
  public get _isWebViewHandler(): _TabletViewOnPressed {
    return this.props._isWebViewHandler;
  }
  public initState() {
    super.initState();

    this.updateObservable("tabletView", null, this.tabletView);

    this.initListeners();

    this.enableBuild = true;
  }
  public initListeners(): void {
    this.on(["isWebView", "tabletView"], this.rebuild);
  }
  public dispose(): void {
    super.dispose();
  }
  public render(): ReactNode {
    return Button({
      decoration: this.isWebView
        ? new ui.BoxDecoration({
            border: ui.Border.all({
              color: new ui.Color(0xff3c3e49),
              width: 0.5,
            }),
            color: new ui.Color(0xffecedf0),
          })
        : new ui.BoxDecoration({
            border: ui.Border.all({
              color: new ui.Color(0xff3c3e49),
              width: 0.5,
            }),
            color: new ui.Color(0xffffffff),
          }),
      disable: this.tabletView.disable,
      onPressed: () => {
        this._isWebViewHandler(this.d3eState);
      },
      onFocusChange: (val) => {},
      child: IconView({
        icon: MaterialIcons.desktop_windows,
        color: new ui.Color(0xff3c3e49),
        size: 15,
        className: "x1a",
      }),
    });
  }
}
function TabletViewWithState(props: TabletViewWithStateProps) {
  return React.createElement(_TabletViewWithState, props);
}

interface MobileViewWithStateProps extends BaseUIProps {
  key?: string;
  d3eState: TemplateOverViewRefs;
  _isMobileViewHandler?: _MobileViewOnPressed;
  isMobileView: boolean;
}

class MobileViewState extends ObjectObservable {
  private _disable: boolean = false;
  public get disable(): boolean {
    return this._disable;
  }
  public setDisable(val: boolean) {
    let isValChanged: boolean = this._disable !== val;

    if (!isValChanged) {
      return;
    }

    this._disable = val;

    this.fire("disable", this);
  }
}

class _MobileViewWithState extends ObservableComponent<MobileViewWithStateProps> {
  mobileViewFocusNode: ui.FocusNode = new ui.FocusNode();
  public constructor(props: MobileViewWithStateProps) {
    super(props);

    this.initState();
  }
  public get isMobileView(): boolean {
    return this.props.isMobileView;
  }
  public get mobileView(): MobileViewState {
    return this.props.d3eState.mobileView;
  }
  public get d3eState(): TemplateOverViewRefs {
    return this.props.d3eState;
  }
  public get _isMobileViewHandler(): _MobileViewOnPressed {
    return this.props._isMobileViewHandler;
  }
  public initState() {
    super.initState();

    this.updateObservable("mobileView", null, this.mobileView);

    this.initListeners();

    this.enableBuild = true;
  }
  public initListeners(): void {
    this.on(["isMobileView", "mobileView"], this.rebuild);
  }
  public dispose(): void {
    super.dispose();
  }
  public render(): ReactNode {
    return ui.Container({
      margin: ui.EdgeInsets.all(5.0, new Map()),
      child: Button({
        decoration: this.isMobileView
          ? new ui.BoxDecoration({
              border: ui.Border.all({
                color: new ui.Color(0xff3c3e49),
                width: 0.5,
              }),
              color: new ui.Color(0xffecedf0),
            })
          : new ui.BoxDecoration({
              border: ui.Border.all({
                color: new ui.Color(0xff3c3e49),
                width: 0.5,
              }),
              color: new ui.Color(0xffffffff),
            }),
        disable: this.mobileView.disable,
        onPressed: () => {
          this._isMobileViewHandler(this.d3eState);
        },
        onFocusChange: (val) => {},
        child: IconView({
          icon: MaterialIcons.phone_iphone,
          color: new ui.Color(0xff3c3e49),
          size: 15,
          className: "x764",
        }),
      }),
      className: "x577",
    });
  }
}
function MobileViewWithState(props: MobileViewWithStateProps) {
  return React.createElement(_MobileViewWithState, props);
}

class _TemplateOverViewState extends ObservableComponent<TemplateOverViewProps> {
  static defaultProps = { obj: null, onAddTemplate: null };
  d3eState: TemplateOverViewRefs = new TemplateOverViewRefs();
  features: Array<D3EFeature> = ListWrapper.widget(this, "features");
  selectedFeature: D3EFeature = null;
  prev: number = 0;
  next: number = 4;
  isMobileView: boolean = true;
  isWebView: boolean = false;
  isOverView: boolean = false;
  featureDetailsPopup: Popup;
  static contextType = BuildContext;
  context: React.ContextType<typeof BuildContext>;
  public helpRefPopupTargetController: PopupTargetController =
    new PopupTargetController();
  public constructor(props: TemplateOverViewProps) {
    super(props);

    this.initState();
  }
  public get obj(): D3EAppTemplate {
    return this.props.obj;
  }
  public initState() {
    super.initState();

    this.initListeners();

    this.enableBuild = true;

    this.onInit();
  }
  public initListeners(): void {
    this.updateSyncProperty("obj", this.props.obj);

    this.on(["obj", "obj.features"], this.computeFeatures);

    this.computeFeatures();

    this.on(
      [
        "features",
        "isMobileView",
        "isOverView",
        "isWebView",
        "next",
        "obj",
        "obj.description",
        "obj.features",
        "obj.icon",
        "obj.title",
        "prev",
        "selectedFeature",
        "selectedFeature.androidScreenshot",
        "selectedFeature.title",
        "selectedFeature.webScreenshot",
      ],
      this.rebuild
    );
  }
  public componentDidUpdate(prevProps: TemplateOverViewProps): void {
    super.componentDidUpdate(prevProps);

    if (prevProps.obj !== this.props.obj) {
      this.updateObservable("obj", prevProps.obj, this.props.obj);

      this.fire("obj", this);
    }
  }
  public setFeatures(val: Array<D3EFeature>): void {
    let isValChanged: boolean = CollectionUtils.isNotEquals(this.features, val);

    if (!isValChanged) {
      return;
    }

    this.updateObservableColl("features", this.features, val);

    this.features.clear();

    this.features.addAll(val);

    this.fire("features", this);
  }
  public addToFeatures(val: D3EFeature, index: number = -1): void {
    if (index === -1) {
      if (!this.features.contains(val)) this.features.add(val);
    } else {
      this.features.remove(this.features.elementAt(index));

      this.features.add(val);
    }

    this.fire("features", this, val, true);

    this.updateObservable("features", null, val);
  }
  public removeFromFeatures(val: D3EFeature): void {
    this.features.remove(val);

    this.fire("features", this, val, false);

    this.removeObservable("features", val);
  }
  public computeFeatures = (): void => {
    try {
      this.setFeatures(Array.from(this.obj.features));
    } catch (exception) {
      console.log(" exception in computeFeatures : " + exception.toString());

      this.setFeatures([]);
    }
  };
  public setSelectedFeature(val: D3EFeature): void {
    let isValChanged: boolean = this.selectedFeature !== val;

    if (!isValChanged) {
      return;
    }

    this.updateObservable("selectedFeature", this.selectedFeature, val);

    this.selectedFeature = val;

    this.fire("selectedFeature", this);
  }
  public setPrev(val: number): void {
    let isValChanged: boolean = this.prev !== val;

    if (!isValChanged) {
      return;
    }

    this.prev = val;

    this.fire("prev", this);
  }
  public setNext(val: number): void {
    let isValChanged: boolean = this.next !== val;

    if (!isValChanged) {
      return;
    }

    this.next = val;

    this.fire("next", this);
  }
  public setIsMobileView(val: boolean): void {
    let isValChanged: boolean = this.isMobileView !== val;

    if (!isValChanged) {
      return;
    }

    this.isMobileView = val;

    this.fire("isMobileView", this);
  }
  public setIsWebView(val: boolean): void {
    let isValChanged: boolean = this.isWebView !== val;

    if (!isValChanged) {
      return;
    }

    this.isWebView = val;

    this.fire("isWebView", this);
  }
  public setIsOverView(val: boolean): void {
    let isValChanged: boolean = this.isOverView !== val;

    if (!isValChanged) {
      return;
    }

    this.isOverView = val;

    this.fire("isOverView", this);
  }
  public render(): ReactNode {
    let cStyle = this.context.theme;

    return ui.Column({
      crossAxisAlignment: ui.CrossAxisAlignment.start,
      children: [
        ui.Row({
          mainAxisAlignment: ui.MainAxisAlignment.end,
          children: [
            ui.Container({
              margin: ui.EdgeInsets.all(5.0, new Map()),
              child: OutlineFloatingActionButton({
                icon: MaterialIcons.close,
                iconColor: new ui.Color(0xff000000),
                buttonBorderColor: new ui.Color(0xff000000),
                iconSize: 15,
                onPressed: () => {
                  this.closeRefHandler(this.d3eState);
                },
              }),
              key: "0",
              className: "x92ac vc",
            }),
          ],
          className: "x701 hc vc h",
          key: "0",
        }),
        ui.Row({
          mainAxisAlignment: ui.MainAxisAlignment.start,
          crossAxisAlignment: ui.CrossAxisAlignment.start,
          children: [
            ui.Column({
              crossAxisAlignment: ui.CrossAxisAlignment.start,
              children: [
                this.selectedFeature !== null
                  ? ui.Container({
                      clipBehavior: ui.Clip.hardEdge,
                      child: ui.NetworkImage({
                        url: this.isOverView
                          ? this.selectedFeature.webScreenshot?.downloadUrl ??
                            "" + "?w=500&h=300"
                          : this.selectedFeature.androidScreenshot
                              ?.downloadUrl ?? "" + "?w=500&h=300",
                        fit: ui.BoxFit.contain,
                        className: "hc vc",
                      }),
                      className: "x14e hc vc",
                    })
                  : [],
                ui.Row({
                  mainAxisAlignment: ui.MainAxisAlignment.center,
                  children: [
                    ui.Wrap({
                      spacing: 10,
                      children: [
                        TextView({
                          data: this.obj.features.isNotEmpty
                            ? (
                                this.obj.features.indexOf(
                                  this.selectedFeature
                                ) + 1
                              ).toString() +
                              "/" +
                              this.obj.features.length.toString()
                            : "",
                          style: new ui.TextStyle({
                            fontSize: 20,
                            color: new ui.Color(0xff9b9fb1),
                          }),
                          className: "xcd9",
                          key: "0",
                        }),
                        TextView({
                          data:
                            this.selectedFeature !== null
                              ? this.selectedFeature.title
                              : "",
                          style: new ui.TextStyle({
                            fontSize: 20,
                            color: new ui.Color(0xff3c3e49),
                            fontWeight: ui.FontWeight.bold,
                          }),
                          className: "xa9f",
                          key: "1",
                        }),
                        IconButton({
                          icon: MaterialIcons.info_outline,
                          color: new ui.Color(0xff9b9fb1),
                          size: 20,
                          onPressed: () => {
                            this.helpRefHandler(this.d3eState);
                          },
                          d3eRef: this.helpRefPopupTargetController.handleRef,
                          key: "2",
                        }),
                      ],
                      key: "0",
                    }),
                  ],
                  className: "x426 hc h",
                  key: "1",
                }),
                ui.Row({
                  mainAxisAlignment: ui.MainAxisAlignment.center,
                  children: [
                    MobileViewWithState({
                      d3eState: this.d3eState,
                      _isMobileViewHandler: this.isMobileViewHandler,
                      isMobileView: this.isMobileView,
                      key: "0",
                    }),
                    TabletViewWithState({
                      d3eState: this.d3eState,
                      _isWebViewHandler: this.isWebViewHandler,
                      isWebView: this.isWebView,
                      key: "1",
                    }),
                  ],
                  className: "x45b hc h",
                  key: "2",
                }),
                this.features.isNotEmpty
                  ? ui.Row({
                      mainAxisAlignment: ui.MainAxisAlignment.start,
                      crossAxisAlignment: ui.CrossAxisAlignment.center,
                      children: [
                        IconButton({
                          icon: MaterialIcons.arrow_back_ios,
                          color: new ui.Color(0xff9b9fb1),
                          tooltip: "Previous",
                          size: 40,
                          onPressed: () => {
                            this.previousButtonHandler(this.d3eState);
                          },
                          states: ui.joinStates(
                            { "data-visibility": this.prev > 3 ? true : false },
                            {}
                          ),
                          key: "0",
                        }),
                        this.features
                          .sublist(this.prev, this.next)
                          .expand((item) => [
                            TemplateFeatureView({
                              obj: item,
                              isOverView: this.isOverView,
                              onSelect: (feature) => {
                                this.featureView(feature, item, this.d3eState);
                              },
                              key: item?.ident,
                            }),
                          ]),
                        IconButton({
                          icon: MaterialIcons.arrow_forward_ios,
                          color: new ui.Color(0xff9b9fb1),
                          tooltip: "Next",
                          size: 40,
                          onPressed: () => {
                            this.nextButtonHandler(this.d3eState);
                          },
                          states: ui.joinStates(
                            {
                              "data-visibility":
                                this.next < this.features.length ? true : false,
                            },
                            {}
                          ),
                          key: "2",
                        }),
                      ],
                      className: "x1c73 hc h",
                    })
                  : [],
                TextView({
                  data: "These designs are representative of the features included in the app.They do not show your finished product. You can customise how everything looks with our network of designers",
                  style: new ui.TextStyle({
                    color: new ui.Color(0xff9b9fb1),
                    fontSize: 12,
                  }),
                  className: "xf63 hc",
                  key: "4",
                }),
              ],
              className: "xde6 hc",
              key: "0",
            }),
            ui.Column({
              mainAxisAlignment: ui.MainAxisAlignment.start,
              crossAxisAlignment: ui.CrossAxisAlignment.start,
              children: [
                ui.Wrap({
                  crossAxisAlignment: ui.WrapCrossAlignment.center,
                  spacing: 5,
                  children: [
                    ui.NetworkImage({
                      url: this.obj.icon?.downloadUrl ?? "",
                      height: 40,
                      width: 50,
                      className: "x1b hc vc",
                      key: "0",
                    }),
                    TextView({
                      data: this.obj.title,
                      style: new ui.TextStyle({
                        fontSize: 36,
                        color: new ui.Color(0xff3c3e49),
                        fontWeight: ui.FontWeight.bold,
                      }),
                      className: "xab9 hc",
                      key: "1",
                    }),
                  ],
                  className: "xc1 hc",
                  key: "0",
                }),
                TextView({
                  data: this.features.isNotEmpty
                    ? this.features.length.toString() + "  Feature Included"
                    : "",
                  style: new ui.TextStyle({
                    fontSize: 14,
                    color: new ui.Color(0xff9b9fb1),
                  }),
                  className: "x90 hc",
                  key: "1",
                }),
                TextView({
                  data: this.obj.description,
                  style: new ui.TextStyle({ fontSize: 14 }),
                  className: "xdf9 hc",
                  key: "2",
                }),
              ],
              className: "xdbd hc",
              key: "1",
            }),
          ],
          className: "x995 hc h",
          key: "1",
        }),
        Divider({ className: "xa39 hc h", key: "2" }),
        ui.Row({
          mainAxisAlignment: ui.MainAxisAlignment.end,
          children: [
            AddTemplateButtonWithState({
              d3eState: this.d3eState,
              _addTemplateButtonHandler: this.addTemplateButtonHandler,
              key: "0",
            }),
          ],
          className: "x56b hc vc h",
          key: "3",
        }),
      ],
      className: ui.join(this.props.className, "TemplateOverView xa76 hc vc"),
      ...copyBaseUIProps(this.props),
    });
  }
  public onInit = (): void => {
    this.setSelectedFeature(this.features.isNotEmpty ? this.features[0] : null);
  };
  public featureView = (
    feature: D3EFeature,
    item: D3EFeature,
    d3eState: TemplateOverViewRefs
  ): void => {
    this.setSelectedFeature(feature);
  };
  public closeRefHandler = (d3eState: TemplateOverViewRefs): void => {
    this.navigator.close();
  };
  public previousButtonHandler = (d3eState: TemplateOverViewRefs): void => {
    if (this.prev > 0) {
      this.setPrev(this.prev - 4);

      this.setNext(this.next - 4);
    }
  };
  public nextButtonHandler = (d3eState: TemplateOverViewRefs): void => {
    if (this.next < this.features.length) {
      this.setPrev(this.prev + 4);

      this.setNext(this.next + 4);
    }
  };
  public isMobileViewHandler = (d3eState: TemplateOverViewRefs): void => {
    this.setIsMobileView(true);

    this.setIsWebView(false);

    this.setIsOverView(false);
  };
  public isWebViewHandler = (d3eState: TemplateOverViewRefs): void => {
    this.setIsMobileView(false);

    this.setIsWebView(true);

    this.setIsOverView(true);
  };
  public helpRefHandler = (d3eState: TemplateOverViewRefs): void => {
    this.showFeatureDetails({ autoClose: true });
  };
  public addTemplateButtonHandler = (d3eState: TemplateOverViewRefs): void => {
    this.onAddTemplate(this.obj);
  };
  public get onAddTemplate(): _TemplateOverViewOnAddTemplate {
    return this.props.onAddTemplate;
  }
  public dispose(): void {
    this.featureDetailsPopup?.dispose();

    super.dispose();
  }
  public showFeatureDetails(
    d3eParams?: Partial<{
      autoClose: boolean;
      model: boolean;
      float: boolean;
      takeFocus: boolean;
    }>
  ): void {
    let autoClose = d3eParams?.autoClose;

    let model = d3eParams?.model;

    let float = d3eParams?.float;

    let takeFocus = d3eParams?.takeFocus;

    this.featureDetailsPopup?.dispose();

    let target: ui.Rect = this.helpRefPopupTargetController.getTarget(
      this.context
    );

    this.featureDetailsPopup = new Popup({
      autoClose: autoClose,
      model: model,
      float: float,
      takeFocus: takeFocus,
      position: ui.PopUpPosition.Top,
      child: ui.Container({
        width: 400,
        child: FeatureDetailsView({ info: this.selectedFeature.description }),
        className: "x0e9 hc vc",
      }),
      target: target,
    });

    this.featureDetailsPopup.showPopup(this.context);
  }
  public hideFeatureDetails(): void {
    this.featureDetailsPopup?.dispose();
  }
  public get navigator(): PageNavigator {
    return PageNavigator.of(this.context);
  }
  public get addTemplateButton() {
    return this.d3eState.addTemplateButton;
  }
  public get mobileView() {
    return this.d3eState.mobileView;
  }
  public get tabletView() {
    return this.d3eState.tabletView;
  }
}
export default function TemplateOverView(props: TemplateOverViewProps) {
  return React.createElement(_TemplateOverViewState, {
    ..._TemplateOverViewState.defaultProps,
    ...props,
  });
}
